import React, { useRef, useState, useMemo, FC } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  type Chart as ChartTypeType
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { EMQXMonthlyCostProps } from '../constants/types';
import { useAvailableYears } from '../hooks/useAvailableYears';
import { useMongoDBMonthlyCostData } from '../hooks/useMongoDBMonthlyCostData';
import { useMongoDBMonthlyCostChartData, useMongoDBExpandedMonthlyCostChartData } from '../hooks/useMongoDBMonthlyCostChartData';
import { useMongoDBMonthlyCostOptions, useMongoDBExpandedMonthlyCostOptions} from '../hooks/useMongoDBMonthlyCostChartOptions';
import { MongoDBMonthlyCostData } from '../constants/types';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

const EMQXMonthlyCost: FC<EMQXMonthlyCostProps> = ({ token}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const chartRef = useRef<ChartTypeType<'bar'> | null>(null);
  const expandedChartRef = useRef<ChartTypeType<'bar'> | null>(null);
  
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);

  const years = useAvailableYears();

  const { initialData, graphData, hasConfirmed, error } = useMongoDBMonthlyCostData(token, year);

  const fullLabels = useMemo(() => initialData!.map(item => item.month), [initialData]);
  const fullDataPoints = useMemo(() => initialData?.map(item => ({ month: item.month, ...item.cost })) || [],[initialData]);
  
  const chartData = useMongoDBMonthlyCostChartData(fullLabels, fullDataPoints);
  const options = useMongoDBMonthlyCostOptions(chartRef, fullDataPoints);
  const expandedData = useMongoDBExpandedMonthlyCostChartData(hasConfirmed, graphData, initialData);
  const expandedOptions = useMongoDBExpandedMonthlyCostOptions(expandedChartRef, graphData, fullLabels, fullDataPoints, hasConfirmed);

  if (!(initialData!.length)) {
    return <div className="text-center text-gray-500 p-4">No data available</div>;
  }

  return (
    <div className="relative transition-all duration-300 w-[400px] h-[300px]">
      <button className="absolute cursor-pointer z-10 top-0 right-2 text-[#2d2d2d]" onClick={() => setIsExpanded(true)} aria-label="Expand chart">
        <div className="icon-wrapper">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="common-svg true" data-for="ClockUsageWidgetfullIcontip" data-tip="">
            <path d="M1.042 5.118V1.043h4.075M5.117 18.958H1.042v-4.075M18.958 14.883v4.075h-4.075M14.883 1.043h4.075v4.075" stroke="#8A8A8A" stroke-width="1.5" stroke-miterlimit="10"></path>
          </svg>
        </div>
      </button>

      <Bar ref={chartRef} data={chartData} options={options} />

      {isExpanded && (
        <>
          <div
            className="fixed bg-[rgba(0,0,0,0.55)] z-[1499] top-0 left-0 w-[100vw] h-[100vh]"
            onClick={() => setIsExpanded(false)}
          />

          <div className="fixed w-[1200px] h-[650px] bg-white shadow-lg p-4 rounded-lg z-[1500] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[#2d2d2d]">
            <button
              onClick={() => setIsExpanded(false)}
              className="absolute right-5 top-5 font-bold text-lg cursor-pointer mb-2" aria-label="Shrink chart"
            >
              <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17.48 9.422l-.902-.902L13 12.098 9.422 8.52l-.902.902L12.098 13 8.52 16.578l.902.902L13 13.902l3.578 3.578.902-.902L13.902 13l3.578-3.578z" fill="#3E3E3E"></path></svg>
            </button>

          <div className="flex items-center mt-10 mb-4">
              <div className="flex items-center space-x-4">
                  <label htmlFor="year" className="font-sans block font-semibold mb-1 text-[#2d2d2d] text-base">Select Year:</label>
                  <select id="year" value={year} onChange={e => { setYear(Number(e.target.value))}} className="font-sans border rounded p-1 w-auto text-[#2d2d2d] text-base">
                    {years.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
              </div>
          </div>
            {error && <p className="text-red-600 text-sm mb-2">{error}</p>}

            {/* Only show chart if no error */}
            {!error && (
              <div className="h-[450px]">
                <Bar ref={expandedChartRef} data={expandedData} options={expandedOptions} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default EMQXMonthlyCost;